import { FLOOR_CONFIG } from '@/lib/campaigns/evidence-floors';
import { formatCentAmount } from '@/lib/campaigns/paid-money';
import {
  GOOGLE_PURCHASE_COUNTED_LINE,
  GOOGLE_PURCHASE_NOT_READ_LINES,
  type GooglePurchasesNotReadReason,
  type GoogleReadsPurchases,
  googlePurchaseNotReadNote,
  googlePurchaseTargetLine,
  notReadReason,
} from '@/lib/paid-media/google/purchase-read';
import type { PlanOutcome } from '@/lib/paid-plan/offer';
import type { PlanSource } from '@/lib/paid-plan/types';

/**
 * The measure of a campaign Mora created (board ruling NOTICE-AND-MEASURE point 2, step 5). A campaign
 * made from a plan is judged against what it was made for: the provider's own count of the plan's
 * result and, when the person set one, their target cost per result. Pure.
 *
 * Rules held here:
 *  - Below the evidence floors the words carry the campaign's own figures against the floors and give
 *    no direction: "Too early to judge: 3 of 25 results, 4 of 7 days".
 *  - Above them the verdict is one of "On target", "Above target" or "Below target", worded with the
 *    cost per result, the target and the window, and the provider's name. "Above" means the cost is
 *    higher than the target (worse); "Below" means lower (better).
 *  - No target on file reads "No target set" with the evidence only. A target is never invented.
 *  - Results are the one provider's own count. Nothing is inferred across providers or from store orders.
 *  - A campaign Mora did not create, or one with no plan, has no measure (evidence only).
 *  - Unknown is never zero: an unread count, spend or delivery gives no measure.
 *  - A thin measure (bookings) follows the floor config: a verdict only with a target and the thin window.
 */

export type MeasureState =
  'too_early' | 'no_target' | 'on_target' | 'above_target' | 'below_target';

export type MeasureTone = 'neutral' | 'good' | 'warning';

/** Green means under the target; a cost within the band is not printed in the colour of success. */
function measureToneFor(state: MeasureState): MeasureTone {
  if (state === 'above_target') return 'warning';
  if (state === 'below_target') return 'good';
  return 'neutral';
}

export const MEASURE_WORD: Readonly<Record<MeasureState, string>> = {
  too_early: 'Too early to judge',
  no_target: 'No target set',
  on_target: 'Working',
  above_target: 'Not working',
  below_target: 'Working',
};

/**
 * Cost within this share of the target reads "On target". Seat judgment, unverified; a verified band
 * replaces it here.
 */
export const ON_TARGET_BAND = 0.1;

export const NO_TARGET_SET_NOTE =
  'Tell Mora what one result is worth to you, and it can say whether the cost is working.';

/**
 * What the provider's count actually counts. Only a conversion count can be a plan's result;
 * impressions, clicks, engagements, page views and conversations never are, and a bare Google
 * "conversions" count is unclassified (its category is not read), so it never matches.
 */
export type CountedKind = 'purchase' | 'lead' | 'conversion_unclassified' | 'non_conversion';

const LABEL_KINDS: Readonly<Record<string, CountedKind>> = {
  purchases: 'purchase',
  orders: 'purchase',
  leads: 'lead',
  'sign ups': 'lead',
  'sign-ups': 'lead',
  conversions: 'conversion_unclassified',
};

/** The kind a provider results label counts. An unknown or empty label is not a conversion. */
export function countedKindOfLabel(label: string | null | undefined): CountedKind {
  return LABEL_KINDS[(label ?? '').trim().toLowerCase()] ?? 'non_conversion';
}

/** Whether a count of this kind is the result the plan was made for. */
export function outcomeAcceptsCount(
  outcome: string | null | undefined,
  kind: CountedKind,
): boolean {
  if (outcome === 'purchases') return kind === 'purchase';
  if (outcome === 'sign_ups' || outcome === 'bookings') return kind === 'lead';
  // A waitlist is counted from the site's own sign-ups (`measureWaitlist`), never from a channel's count.
  return false;
}

export interface MeasureInput {
  /** The plan's result: purchases, sign_ups or bookings. A waitlist has its own measure, `measureWaitlist`. */
  outcome: string | null;
  /** What `results` counts; a count that does not match the outcome gives no measure. */
  counted: CountedKind;
  /** True only for a campaign Mora created from a plan. */
  madeFromPlan: boolean;
  /** 'linkedin' is evidence only. */
  channel: 'meta' | 'google' | 'linkedin';
  /** The provider's name as the person reads it: "Meta", "Google". */
  providerName: string;
  /** The provider's own count of the plan's result in the window; null when not read. */
  results: number | null;
  /** Spend in the window in the account currency's major unit; null when not read. */
  spend: number | null;
  /** Days with spend in the window; null when not read. */
  daysDelivered: number | null;
  /** Length of the window in days; null when not known. */
  windowDays: number | null;
  /** The person's target cost per result in the major unit; null when none is set. */
  target: number | null;
  currencyCode: string | null;
  /** Singular noun the provider counted: "order", "lead", "conversion". */
  noun: string;
  /** A measure expected to stay under the result floor in a month (bookings). */
  thin?: boolean;
}

export interface CampaignMeasure {
  state: MeasureState;
  word: string;
  tone: MeasureTone;
  /** One sentence, the word first. */
  headline: string;
  /** What it was read from: provider, window, counts. */
  support: string;
  /** The short form for a list row. */
  listLine: string;
  costPerResult: number | null;
  /** Set only below the floors: what the campaign still needs, against what it has. Words, no direction. */
  needs?: string;
  /** With `needs`: true when only time or results are missing, so waiting is the whole answer. False when the person's target is missing too. */
  waitOnly?: boolean;
  /**
   * Set only for a direction against a target: the page's sentence in three parts, so the cost figure
   * alone can carry the verdict colour. `lead + cost + tail` reads as one sentence, the source named in it.
   */
  sentence?: { lead: string; cost: string; tail: string };
}

const MULTIPLE_WORDS: Readonly<Record<number, string>> = {
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
};

/** "under your target", "close to ...", "over ...", or "about three times ..." once the cost is far past it. The target's amount is printed once, in the Made for row. */
export function targetPhrase(state: MeasureState, cost: number, target: number): string {
  if (state === 'below_target') return 'under your target';
  if (state === 'on_target') return 'close to your target';
  const times = Math.round(cost / target);
  if (times >= 3 && cost >= target * 3) {
    return `about ${MULTIPLE_WORDS[times] ?? times} times your target`;
  }
  return 'over your target';
}

export { planTargetCost } from '@/lib/campaigns/campaign-target';

/** Thin measures: a plan whose result is a booking. */
export function isThinOutcome(outcome: string | null | undefined): boolean {
  return outcome === 'bookings';
}

function plural(count: number, noun: string): string {
  const shown = count.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return `${shown} ${noun}${count === 1 ? '' : 's'}`;
}

function windowWords(windowDays: number | null): string {
  return windowDays === null ? 'the window read' : `the last ${windowDays} days`;
}

/**
 * What a campaign below the floors still lacks, naming only the parts that are short and giving each
 * its unit. `waitOnly` is false when the person's target is among them, because time alone will not
 * supply that.
 */
function missingParts(input: {
  daysDelivered: number;
  dayFloor: number;
  results: number | null;
  resultFloor: number | null;
  resultNoun: string;
  needsTarget: boolean;
  targetNoun: string;
}): { needs: string; waitOnly: boolean } {
  const shortDays = input.daysDelivered < input.dayFloor;
  const shortResults =
    input.results !== null && input.resultFloor !== null && input.results < input.resultFloor;
  const needed: string[] = [];
  const has: string[] = [];
  if (shortDays) {
    needed.push(`about ${input.dayFloor} days of delivery`);
    has.push(`${input.daysDelivered} ${input.daysDelivered === 1 ? 'day' : 'days'}`);
  }
  if (shortResults && input.results !== null && input.resultFloor !== null) {
    needed.push(`${input.resultFloor} ${input.resultNoun}s`);
    has.push(plural(input.results, input.resultNoun));
  }
  if (input.needsTarget) needed.push(`a target cost per ${input.targetNoun}`);
  const list =
    needed.length > 1
      ? `${needed.slice(0, -1).join(', ')} and ${needed[needed.length - 1]}`
      : (needed[0] ?? 'more delivery');
  const have = has.length > 0 ? `; it has ${has.join(' and ')}` : '';
  return { needs: `Needs ${list}${have}.`, waitOnly: !input.needsTarget };
}

export function measureCampaign(input: MeasureInput): CampaignMeasure | null {
  if (!input.madeFromPlan || input.channel === 'linkedin') return null;
  // A bare Google conversion count is never read as the plan's result, so it never earns a direction.
  // Below the floors the campaign's own figures still say what is missing, which is true whatever the
  // count is called.
  const unclassified = input.counted === 'conversion_unclassified';
  if (!outcomeAcceptsCount(input.outcome, input.counted) && !unclassified) return null;
  const { results, spend, daysDelivered } = input;
  if (results === null || spend === null || daysDelivered === null) return null;

  const by = input.providerName;
  const noun = input.noun;
  const resultFloor = FLOOR_CONFIG.verdictResults.value;
  const dayFloor = input.thin ? FLOOR_CONFIG.thinVerdictDays.value : FLOOR_CONFIG.verdictDays.value;
  const target = input.target !== null && input.target > 0 ? input.target : null;
  const cost = results > 0 ? spend / results : null;
  const window = windowWords(input.windowDays);
  const counted = `${by} reports ${plural(results, noun)} for ${formatCentAmount(spend, input.currencyCode)} over ${window}`;

  const floorsMet = input.thin
    ? target !== null && daysDelivered >= dayFloor
    : results >= resultFloor && daysDelivered >= dayFloor;

  if (!floorsMet) {
    const figures = input.thin
      ? `${plural(results, 'result')}, ${daysDelivered} of ${dayFloor} days`
      : `${results.toLocaleString(undefined, { maximumFractionDigits: 1 })} of ${resultFloor} results, ${daysDelivered} of ${dayFloor} days`;
    const line = `${MEASURE_WORD.too_early}: ${figures}`;
    const { needs, waitOnly } = missingParts({
      daysDelivered,
      dayFloor,
      results: input.thin ? null : results,
      resultFloor: input.thin ? null : resultFloor,
      resultNoun: 'result',
      needsTarget: input.thin && target === null,
      targetNoun: noun,
    });
    return {
      needs,
      waitOnly,
      state: 'too_early',
      word: MEASURE_WORD.too_early,
      tone: 'neutral',
      headline: `${MEASURE_WORD.too_early}.`,
      support: `${by} reports ${formatCentAmount(spend, input.currencyCode)} spent so far. Mora gives no direction until there is enough to read.`,
      listLine: line,
      costPerResult: cost,
    };
  }

  if (unclassified) return null;

  if (target === null) {
    const costText = cost === null ? null : formatCentAmount(cost, input.currencyCode);
    return {
      state: 'no_target',
      word: MEASURE_WORD.no_target,
      tone: 'neutral',
      headline: `${MEASURE_WORD.no_target}. ${counted}${costText ? `, ${costText} each` : ''}.`,
      support: NO_TARGET_SET_NOTE,
      listLine: costText
        ? `${MEASURE_WORD.no_target}, ${costText} per ${noun}`
        : MEASURE_WORD.no_target,
      costPerResult: cost,
    };
  }

  const targetText = formatCentAmount(target, input.currencyCode);
  if (cost === null) {
    // Past the window with nothing counted against a target: the cost is unbounded.
    const line = `${MEASURE_WORD.above_target}: no ${noun}s counted in ${daysDelivered} days`;
    return {
      state: 'above_target',
      word: MEASURE_WORD.above_target,
      tone: 'warning',
      headline: `${line}, against your ${targetText} target.`,
      support: `${counted}.`,
      listLine: line,
      costPerResult: null,
    };
  }

  const costText = formatCentAmount(cost, input.currencyCode);
  const state: MeasureState =
    cost > target * (1 + ON_TARGET_BAND)
      ? 'above_target'
      : cost < target * (1 - ON_TARGET_BAND)
        ? 'below_target'
        : 'on_target';
  const tone: MeasureTone = measureToneFor(state);
  const sentence = {
    lead: `${MEASURE_WORD[state]}: `,
    cost: costText,
    tail: ` ${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}, ${targetPhrase(state, cost, target)}.`,
  };
  return {
    state,
    word: MEASURE_WORD[state],
    tone,
    sentence,
    headline: `${sentence.lead}${sentence.cost}${sentence.tail}`,
    support: `${counted}.`,
    listLine: `${MEASURE_WORD[state]}, ${costText} per ${noun}`,
    costPerResult: cost,
  };
}

/**
 * The limitation a waitlist readout always carries (board ruling FIRST-MORA-CAMPAIGN 1.6). Tracking on
 * the site is consent-gated, so the count is a floor; one flight with no control group and a small
 * sample names no winning ad and is not a verdict on the market.
 */
export const WAITLIST_LIMITATION =
  'Sign-ups are counted from visits that carried this campaign’s tags. Visitors who decline tracking on your site are not counted, so the real number is at least this. This is one flight with no control group and a small sample: it names no winning ad and says nothing about the market.';

export interface WaitlistMeasureInput {
  /** Sign-ups on the site whose campaign tag is this plan's; null when they were not read. */
  signups: number | null;
  /** Team sign-ups left out of `signups`; null when not read. Shown, so the exclusion is never silent. */
  excluded?: number | null;
  /** Paid spend of the plan's campaigns in the window, major unit; null when not read. */
  spend: number | null;
  /** Days with spend in the window; null when not read. */
  daysDelivered: number | null;
  /** Length of the window in days; null when not known. */
  windowDays: number | null;
  currencyCode: string | null;
  /** The person's target cost per sign-up in the major unit; null when none is set. */
  target: number | null;
  /** The channels' own reach figure is never used here; sign-ups come from the site only. */
  madeFromPlan: boolean;
}

export interface WaitlistMeasure extends CampaignMeasure {
  limitation: string;
}

/**
 * The measure of a waitlist plan. The result is the site's own count of sign-ups carrying the plan's
 * tag, never a channel's count of clicks or page views. The floors and the words are the ones every
 * other measure uses (EVIDENCE-FLOORS): below them the readout gives the plan's own figures and no
 * direction; above them it compares cost per sign-up with the person's target, and with no target it
 * says so. An unread count, spend or delivery gives no measure.
 */
export function measureWaitlist(input: WaitlistMeasureInput): WaitlistMeasure | null {
  if (!input.madeFromPlan) return null;
  const { signups, spend, daysDelivered } = input;
  if (signups === null || spend === null || daysDelivered === null) return null;

  const noun = 'waitlist sign-up';
  const resultFloor = FLOOR_CONFIG.verdictResults.value;
  const dayFloor = FLOOR_CONFIG.verdictDays.value;
  const target = input.target !== null && input.target > 0 ? input.target : null;
  const cost = signups > 0 ? spend / signups : null;
  const window = windowWords(input.windowDays);
  const counted = `Your site recorded ${signups === 0 ? `no ${noun}s` : `at least ${plural(signups, noun)}`} from tagged visits for ${formatCentAmount(spend, input.currencyCode)} spent over ${window}`;
  const left = input.excluded ?? null;
  const limitation =
    left === null
      ? WAITLIST_LIMITATION
      : `${WAITLIST_LIMITATION} Team sign-ups left out of this count: ${left.toLocaleString()}.`;
  const base = { limitation, costPerResult: cost };

  if (signups < resultFloor || daysDelivered < dayFloor) {
    const line = `${MEASURE_WORD.too_early}: ${signups.toLocaleString(undefined, { maximumFractionDigits: 1 })} of ${resultFloor} results, ${Math.min(daysDelivered, dayFloor)} of ${dayFloor} days`;
    return {
      ...base,
      ...missingParts({
        daysDelivered,
        dayFloor,
        results: signups,
        resultFloor,
        resultNoun: 'sign-up',
        needsTarget: false,
        targetNoun: noun,
      }),
      state: 'too_early',
      word: MEASURE_WORD.too_early,
      tone: 'neutral',
      headline: `${line}.`,
      support: `${counted}. Mora gives no direction until there is enough to read.`,
      listLine: line,
    };
  }

  if (target === null) {
    const costText = cost === null ? null : formatCentAmount(cost, input.currencyCode);
    return {
      ...base,
      state: 'no_target',
      word: MEASURE_WORD.no_target,
      tone: 'neutral',
      headline: `${MEASURE_WORD.no_target}. ${counted}${costText ? `, ${costText} each` : ''}.`,
      support: NO_TARGET_SET_NOTE,
      listLine: costText
        ? `${MEASURE_WORD.no_target}, ${costText} per ${noun}`
        : MEASURE_WORD.no_target,
    };
  }

  const targetText = formatCentAmount(target, input.currencyCode);
  if (cost === null) {
    const line = `${MEASURE_WORD.above_target}: no ${noun}s counted in ${daysDelivered} days`;
    return {
      ...base,
      state: 'above_target',
      word: MEASURE_WORD.above_target,
      tone: 'warning',
      headline: `${line}, against your ${targetText} target.`,
      support: `${counted}.`,
      listLine: line,
    };
  }
  const costText = formatCentAmount(cost, input.currencyCode);
  const state: MeasureState =
    cost > target * (1 + ON_TARGET_BAND)
      ? 'above_target'
      : cost < target * (1 - ON_TARGET_BAND)
        ? 'below_target'
        : 'on_target';
  return {
    ...base,
    state,
    word: MEASURE_WORD[state],
    tone: measureToneFor(state),
    headline: `${MEASURE_WORD[state]}: ${costText} per ${noun}, against your ${targetText} target.`,
    support: `${counted}.`,
    listLine: `${MEASURE_WORD[state]}, ${costText} per ${noun}`,
  };
}

// ---------------------------------------------------------------------------------------------
// What a campaign was made for
// ---------------------------------------------------------------------------------------------

/** What each plan result is called, and the singular noun a cost is quoted per. */
export const OUTCOME_WORDS: Readonly<Record<PlanOutcome, { goal: string; noun: string }>> = {
  purchases: { goal: 'purchases', noun: 'purchase' },
  sign_ups: { goal: 'sign-ups', noun: 'sign-up' },
  bookings: { goal: 'bookings', noun: 'booking' },
  waitlist: { goal: 'waitlist sign-ups', noun: 'waitlist sign-up' },
};

export const NO_TARGET_SENTENCE = 'No target is set.';

export interface PurposeInput {
  /** The plan's result; null when the campaign has no plan, which gives no purpose line. */
  outcome: PlanOutcome | null;
  sourceKind: PlanSource['kind'] | null;
  /** What the plan promotes: the product, offer, Brief or Concept's own name. */
  sourceLabel: string | null;
  providerName: string;
  /** The person's target cost per result in the major unit; null when none is set. */
  target: number | null;
  currencyCode: string | null;
  /**
   * Whether this provider's count is read as the plan's result. False for a channel that reports
   * conversions without a kind, where the sentence must not say Mora reads the goal as the result.
   * Defaults to true.
   */
  providerCountsResult?: boolean;
}

/**
 * One sentence for what a campaign Mora made was made for, read from its plan and nothing else: the
 * result the plan counts, what it promotes and the person's target if there is one. A Brief or Concept
 * plan names no result of its own, so the sentence says Mora reads purchases and why, rather than
 * presenting a goal the person never chose. Null for a campaign with no plan.
 */
export function purposeSentence(input: PurposeInput): string | null {
  if (input.outcome === null) return null;
  const words = OUTCOME_WORDS[input.outcome];
  const label = (input.sourceLabel ?? '').trim();
  const target =
    input.target !== null && input.target > 0
      ? `Target: ${formatCentAmount(input.target, input.currencyCode)} or less per ${words.noun}.`
      : NO_TARGET_SENTENCE;
  if (input.sourceKind === 'brief' || input.sourceKind === 'concept') {
    const kind = input.sourceKind === 'brief' ? 'Brief' : 'Concept';
    const from = label
      ? `Made from the ${kind} “${label}” on ${input.providerName}.`
      : `Made from a ${kind} on ${input.providerName}.`;
    const unread = input.providerCountsResult === false && input.outcome !== 'waitlist';
    const reading = unread
      ? `It names no result of its own, so the plan counts ${words.goal}.`
      : `It names no result of its own, so Mora reads ${words.goal} as the result.`;
    return `${from} ${reading} ${target}`;
  }
  const subject = label
    ? `${label} on ${input.providerName}`
    : `This campaign on ${input.providerName}`;
  return `${subject}, made to bring in ${words.goal}. ${target}`;
}

/** A provider count that is not split by kind is shown, never read as the plan's result. */
export function unclassifiedCountNote(providerName: string, outcome: PlanOutcome): string {
  return `${providerName} reports conversions without saying which kind, so Mora shows the figures and does not read them as ${OUTCOME_WORDS[outcome].goal}.`;
}

/** A provider count that is a different kind from the plan's result is shown, never read as it. */
export function mismatchedCountNote(
  providerName: string,
  countedGoal: string,
  outcome: PlanOutcome,
  googleReason: GooglePurchasesNotReadReason | null = null,
): string {
  if (googleReason !== null) return googlePurchaseNotReadNote(googleReason);
  return `${providerName} counts ${countedGoal} here, not ${OUTCOME_WORDS[outcome].goal}, so Mora shows the figures and does not read them as ${OUTCOME_WORDS[outcome].goal}.`;
}

// ---------------------------------------------------------------------------------------------
// The "Made for" line of the campaign page
// ---------------------------------------------------------------------------------------------

export interface MadeFor {
  /** "purchases of Pro Control Racket on Meta". */
  what: string;
  /** "Target $40.00 or less per purchase", "No target set", or why a channel gets none. */
  target: string;
  /** True when the person can set or change a target here: the channel's count is read as the plan's result. */
  canSetTarget: boolean;
  hasTarget: boolean;
  /** For a Brief or Concept plan: why the result is what it is, shown behind "Why purchases". */
  why: { label: string; text: string } | null;
}

export interface MadeForInput {
  outcome: PlanOutcome | null;
  sourceKind: PlanSource['kind'] | null;
  sourceLabel: string | null;
  providerName: string;
  target: number | null;
  currencyCode: string | null;
  /** Whether the channel's count is read as the plan's result (Meta's purchases; a site's own waitlist count). */
  providerCountsResult: boolean;
  /**
   * Google only: the proof that its conversions are website purchases (`googleReadsPurchases`). When
   * given for a purchases plan it decides the target line and replaces `providerCountsResult`.
   */
  googleProof?: GoogleReadsPurchases | null;
}

/**
 * What a campaign Mora made was made for, split the way the page draws it: the result and the thing,
 * the target (or why this channel has none), and, for a Brief or Concept that named no result, the one
 * sentence behind "Why purchases". A channel that does not label its conversions gets no target, and
 * the line says so in words. Null for a campaign with no plan.
 */
export function madeFor(input: MadeForInput): MadeFor | null {
  if (input.outcome === null) return null;
  const words = OUTCOME_WORDS[input.outcome];
  const label = (input.sourceLabel ?? '').trim();
  const proof = input.outcome === 'purchases' ? (input.googleProof ?? null) : null;
  const countsResult = proof ? proof.reads : input.providerCountsResult;
  const canSetTarget = countsResult || input.outcome === 'waitlist';
  const goalWord = !countsResult && input.outcome === 'purchases' ? 'sales' : words.goal;
  const repeatsGoal = label
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .includes(goalWord.toLowerCase().split(/[^a-z0-9]+/)[0] ?? '');
  const subject = label && !repeatsGoal ? `${goalWord} of ${label}` : goalWord;
  const targetValue = input.target;
  const hasTarget = canSetTarget && targetValue !== null && targetValue > 0;
  const target = proof
    ? !proof.reads
      ? GOOGLE_PURCHASE_NOT_READ_LINES[notReadReason(proof)!]
      : hasTarget
        ? googlePurchaseTargetLine(formatCentAmount(targetValue, input.currencyCode))
        : GOOGLE_PURCHASE_COUNTED_LINE
    : !canSetTarget
      ? `No target: ${input.providerName} conversions are not read as ${words.goal}`
      : hasTarget
        ? `Target ${formatCentAmount(targetValue, input.currencyCode)} or less per ${words.noun}`
        : 'No target set';
  const named = input.sourceKind === 'brief' || input.sourceKind === 'concept';
  const why =
    named && canSetTarget && input.outcome !== 'waitlist'
      ? {
          label: `Why ${words.goal}`,
          text: `It names no result of its own, so Mora reads ${words.goal} as the result.`,
        }
      : null;
  return { what: subject, target, canSetTarget, hasTarget, why };
}
